import { useCallback, useMemo, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MedicalNotePinOption } from "@/Common/Models/Employee/Enums/MedicalNotePinOption";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DropdownButton } from "@/DesignSystem";
import { IconButton } from "@/DesignSystem/Molecules/IconButton";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { NoteStreamlineSettings } from "@/Patient/Models/Settings/NoteStreamlineSettings";
import { StreamlineNoteCompletionHelper } from "../helpers/StreamlineNoteCompletionHelper";
import { useNoteActions } from "../hooks/useNoteActions";

import PushPinIconFilled from "@/DesignSystem/Atoms/Icons/Types/PushPin/push-pin-fill.svg";
import PushPinIconRegular from "@/DesignSystem/Atoms/Icons/Types/PushPin/push-pin-regular.svg";

interface INoteActionButtonsProps {
    isReadOnly?: boolean;
    isDisabled?: boolean;
    appointmentId?: string;
    onApprove?: (onSuccess?: () => void) => Promise<void>;
    dataCy?: string;
}

function MedicalNoteActionButton(props: Readonly<INoteActionButtonsProps>): JSX.Element {
    const { isReadOnly, isDisabled, appointmentId, onApprove, dataCy } = props;
    const { getText } = useTranslation();
    const onNoteActionClick = useNoteActions();
    const allNoteActionConfig = StreamlineNoteCompletionHelper.getNoteActionConfig();
    const readOnlyActionConfig = StreamlineNoteCompletionHelper.getReadOnlyOptions();
    const savedNoteAction = cacheStore.UserSettings.NoteStreamlineSettings?.SavedMedicalNotePinnedAction ?? MedicalNotePinOption.Approve;
    const newNoteAction = cacheStore.UserSettings.NoteStreamlineSettings?.NewMedicalNotePinnedAction ?? MedicalNotePinOption.Save;
    const defaultAction = isReadOnly ? savedNoteAction : newNoteAction;
    const [selectedAction, setSelectedAction] = useState<MedicalNotePinOption>(defaultAction);

    const items = useMemo(() => {
        const filteredConfigs = isReadOnly ? allNoteActionConfig.filter(item => readOnlyActionConfig.includes(item.id)) : allNoteActionConfig;

        return filteredConfigs.map(action => ({
            id: action.id,
            label: getText(action.labelKey),
            onClick: () => onNoteActionClick(action.id, appointmentId, onApprove),
            iconButton: (
                <IconButton
                    onClick={e => {
                        e.stopPropagation();
                        handlePinClick(action.id);
                    }}
                    variant="neutral"
                    category="status-icon"
                    size="sm"
                    dataCy={`noteActionIconDataCy-${action.id}`}
                >
                    {selectedAction === action.id ? <PushPinIconFilled /> : <PushPinIconRegular />}
                </IconButton>
            )
        }));
    }, [isReadOnly, allNoteActionConfig, readOnlyActionConfig, selectedAction]);

    const dropdownLabel = useMemo(() => getText(allNoteActionConfig.find(p => p.id === selectedAction)?.labelKey ?? ""), [allNoteActionConfig, selectedAction]);

    function handlePinClick(action: MedicalNotePinOption): void {
        setSelectedAction(action);
        const isNewActionSameAsSelected = selectedAction === action;

        if (isNewActionSameAsSelected) return;

        if (isReadOnly) {
            saveUserSetting({ SavedMedicalNotePinnedAction: action, NewMedicalNotePinnedAction: newNoteAction });
        }
        else {
            saveUserSetting({ SavedMedicalNotePinnedAction: savedNoteAction, NewMedicalNotePinnedAction: action });
        }
    }

    const saveUserSetting = useCallback(async (model: NoteStreamlineSettings): Promise<void> => {
        const userSettings = new UserSettings({
            NoteStreamlineSettings: NoteStreamlineSettings.fromModel(model)
        });

        const response = await Client.postAsync(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            }
        );

        if (response.isSuccess)
            cacheStore.UserSettings.NoteStreamlineSettings = userSettings.NoteStreamlineSettings;
    }, []);

    return (
        <DropdownButton
            items={items}
            label={dropdownLabel}
            onClick={() => onNoteActionClick(selectedAction, appointmentId, onApprove)}
            direction="topRight"
            disabled={isDisabled}
            dataCy={dataCy}
            toggleDisabled={isDisabled}
        />
    );
}

export { MedicalNoteActionButton };
